/* ---------------------------------------------------------------
 *  admin / DepositSelf.jsx
 * -------------------------------------------------------------- */
import React, { useEffect, useState, useCallback } from "react";
import {
  CheckCircle,
  XCircle,
  Image as ImageIcon,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";
import { Alert, IconButton, Snackbar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const DEPOSIT_API = "https://api.pandabett.com/api/deposit";

/* ----------------------------------------------------------------
 *  util – quick fetch wrapper (json or raw)
 * ---------------------------------------------------------------- */
async function http(url: RequestInfo | URL, opts = {}, raw = false) {
  const res = await fetch(url, opts);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return raw ? res : res.json();
}
/* ------------ types -------------------------------------------------- */
type DepositStatus = "pending" | "approved" | "rejected";
/* tiny cell helpers --------------------------------------------------- */
type CellProps = React.PropsWithChildren<{ className?: string }>;

const Th: React.FC<CellProps> = ({ children, className = "" }) => (
  <th
    className={`px-4 py-2 text-left font-semibold text-gray-600 ${className}`}
  >
    {children}
  </th>
);

const Td: React.FC<CellProps> = ({ children, className = "" }) => (
  <td className={`px-4 py-2 text-gray-800 ${className}`}>{children}</td>
);

/* button that happily forwards all <button> props */
type IconBtnProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

const IconBtn: React.FC<IconBtnProps> = ({
  children,
  className = "",
  ...rest
}) => (
  <button
    {...rest}
    className={`p-1.5 rounded hover:bg-gray-200 disabled:opacity-50 ${className}`}
  >
    {children}
  </button>
);

export interface DepositRow {
  deposit_id: number;
  username: string;
  amount: number;
  transaction_id: string;
  status: DepositStatus;
  created_at: string;   // ISO timestamp
  bank_name: string;
}

interface PreviewShot {
  id: number;
  url: string;
}

/* ----------------------------------------------------------------
 *  MAIN COMPONENT
 * ---------------------------------------------------------------- */
export default function DepositSelf() {
  const staffId = localStorage.getItem("currentUserId"); // ← your upline id


  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState("");
  // {url,id}|null
  const [actionBusy, setAction]   = useState(false);
const [rows,    setRows]    = useState<DepositRow[]>([]);
const [preview, setPreview] = useState<PreviewShot | null>(null);
const [snack, setSnack] = useState({
  open    : false,
  message : "",
  severity: "success" as "success" | "error"
});
const [stats, setStats] = useState({
  pending: 0,
  approved: 0,
  rejected: 0,
});
/* tiny helper */
const showSnack = (msg: string, sev: "success" | "error" = "success") =>
  setSnack({ open: true, message: msg, severity: sev });

  /* -------------------------- load -------------------------------- */
  const loadRows = useCallback(async () => {
    if (!staffId) { setErr("Staff-id missing"); return; }
    setLoading(true);
    try {
    const json: { data: DepositRow[] } =
  await http(`${DEPOSIT_API}/admin/${staffId}`);
setRows(json.data ?? []);
const data = json.data ?? [];
setStats({
      pending: data
        .filter((r) => r.status === "pending")
        .reduce((sum, r) => sum + r.amount, 0),
      approved: data
        .filter((r) => r.status === "approved")
        .reduce((sum, r) => sum + r.amount, 0),
      rejected: data
        .filter((r) => r.status === "rejected")
        .reduce((sum, r) => sum + r.amount, 0),
    });
      setErr("");
    } catch (e) {
      console.error(e);
      setErr("Could not load deposits");
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => { loadRows(); }, [loadRows]);

  /* -------------------------- actions ----------------------------- */
  async function doAction(id: any, type: string) {
    if (actionBusy) return;
    const ok = window.confirm(
      `${type === "approve" ? "Approve" : "Reject"} deposit ${id}?`
    );
    if (!ok) return;

    const comment =
      type === "reject" ? prompt("Comment (optional)", "") || "" : undefined;

    try {
      setAction(true);
     const resp: any =    await http(
        `${DEPOSIT_API}/admin/${type}/${id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", "x-staff-id": staffId },
          body: comment ? JSON.stringify({ admin_comment: comment }) : undefined,
        }
      );
      if (resp?.error === "Insufficient INR") {
    showSnack("Insufficient balance", "error");
    return;                     // ← stop, don’t mutate rows
  }

    setRows((prev) => {
          const updatedRows = prev.map((r) =>
            r.deposit_id === id
              ? { ...r, status: (type === "approve" ? "approved" : "rejected") as DepositStatus }
              : r
          );
          // Update stats based on the new rows
          setStats({
            pending: updatedRows
              .filter((r) => r.status === "pending")
              .reduce((sum, r) => sum + r.amount, 0),
            approved: updatedRows
              .filter((r) => r.status === "approved")
              .reduce((sum, r) => sum + r.amount, 0),
            rejected: updatedRows
              .filter((r) => r.status === "rejected")
              .reduce((sum, r) => sum + r.amount, 0),
          });
          return updatedRows;
        });

    }catch (e: any) {
    /* -------- this is the ONLY change -------- */
    let msg = "Action failed";

    if (e?.error) {
      /* your original case (when helper already parsed JSON) */
      msg = e.error;

    } else if (e instanceof Response) {
      /* fetch() response that http/apiFetch threw */
      try {
        const data = await e.clone().json();      // { error: "Insufficient INR" }
        msg = data?.error || msg;
      } catch {
        /* not JSON?  try plain text */
        try { msg = await e.text(); } catch {}
      }
    }

    showSnack(msg, "error");
    console.error(e);
  } finally {
      // window.location.reload();
      setAction(false);
    }
  }



const openShot = async (row: DepositRow) => {
  try {
    const res = await fetch(
      `${DEPOSIT_API}/${staffId}/${row.deposit_id}/screenshot`
    );
    if (!res.ok) throw new Error("HTTP " + res.status);
    const blob = await res.blob();
    setPreview({
      id : row.deposit_id,
      url: URL.createObjectURL(blob)
    });
  } catch (e) {
    console.error(e);
    alert("Cannot fetch screenshot");
  }
};


  /* -------------------------- ui ---------------------------------- */
  return (
     <div className="ml-0 lg:ml-[256px] p-2">
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
      <div className="mb-4 grid grid-cols-3 gap-4">
  <div className="bg-yellow-50 p-3 rounded">
    <p className="text-sm text-yellow-800">Pending</p>
    <p className="text-lg font-semibold">₹ {stats.pending.toLocaleString()}</p>
  </div>
  <div className="bg-green-50 p-3 rounded">
    <p className="text-sm text-green-800">Approved</p>
    <p className="text-lg font-semibold">₹ {stats.approved.toLocaleString()}</p>
  </div>
  <div className="bg-red-50 p-3 rounded">
    <p className="text-sm text-red-800">Rejected</p>
    <p className="text-lg font-semibold">₹ {stats.rejected.toLocaleString()}</p>
  </div>
</div>

        <button
          onClick={loadRows}
          disabled={loading}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {err && (
        <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded">
          {err}
        </p>
      )}

      <div className="overflow-x-auto bg-white shadow rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="whitespace-nowrap">
              <Th>ID</Th>
              <Th>User</Th>
              <Th>Amount</Th>
              <Th>Txn&nbsp;/&nbsp;UTR</Th>
              <Th>Status</Th>
              <Th>Requested</Th>
              <Th>Bank</Th>
              <Th>Actions</Th>
            </tr>
          </thead>

          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-gray-500">
                  No deposits
                </td>
              </tr>
            )}

            {rows.map((r:any) => (
              <tr
                key={r.deposit_id}
                className="border-t hover:bg-gray-50 whitespace-nowrap"
              >
                <Td>{r.deposit_id}</Td>
                <Td>{r.username}</Td>
                <Td>₹ {Number(r.amount).toLocaleString()}</Td>
                <Td className="font-mono">{r.transaction_id}</Td>
                <Td
                  className={
                    r.status === "pending"
                      ? "text-yellow-600"
                      : r.status === "approved"
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  {r.status}
                </Td>
                <Td>{format(new Date(r.created_at), "dd MMM yy HH:mm")}</Td>
                <Td>{r.bank_name}</Td>
                <Td>
                  <div className="flex gap-2">
                    <IconBtn title="View slip" onClick={() => openShot(r)}>
                      <ImageIcon size={18} />
                    </IconBtn>

                    {r.status === "pending" && (
                      <>
                        <IconBtn
                          title="Approve"
                          onClick={() => doAction(r.deposit_id, "approve")}
                        >
                          <CheckCircle size={18} className="text-green-600" />
                        </IconBtn>
                        <IconBtn
                          title="Reject"
                          onClick={() => doAction(r.deposit_id, "reject")}
                        >
                          <XCircle size={18} className="text-red-600" />
                        </IconBtn>
                      </>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>

        {loading && (
          <p className="p-4 text-center text-gray-500">Loading…</p>
        )}
      </div>

      {/* ------------------ screenshot modal ----------------------- */}
      {preview && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
          <div className="relative bg-white p-4 rounded shadow-lg max-w-full max-h-full">
            <button
              onClick={() => {
                URL.revokeObjectURL(preview.url);
                setPreview(null);
              }}
              className="absolute top-2 right-2 text-gray-600 hover:text-black text-xl"
            >
              &times;
            </button>
            <img
              src={preview.url}
              alt="Payment slip"
              className="max-h-[80vh] max-w-[90vw] object-contain"
            />
          </div>
        </div>
      )}
    </div>
    <Snackbar
  open={snack.open}
  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
  autoHideDuration={null}                     // ← stays until closed
  onClose={() => setSnack(s => ({ ...s, open: false }))}
>
  <Alert
    variant="filled"
    severity={snack.severity}
    action={
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={() => setSnack(s => ({ ...s, open: false }))}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    }
  >
    {snack.message}
  </Alert>
</Snackbar>

    </div>
  );
}

/* tiny cell helpers */
// (removed duplicate Th, Td, and IconBtn declarations)
